import axios, { CancelTokenSource } from "axios";

import notificationStore from "@client/stores/NotificationStore";

type Config = {
  method?: "POST" | "GET" | "DELETE" | "PUT" | "PATCH";
  url: string;
  data?: any;
  quietError?: boolean;
  cancelId?: string;
};

const redirect = (statusCode: number) => {
  if (statusCode !== 401 || window.location.pathname.indexOf("login") !== -1) {
    return;
  }
  let currentPath = "?prev=/";
  if (window.location.pathname !== "/") {
    currentPath = `?prev=${window.location.pathname.replace(env.BASENAME, "")}`;
  }
  window.location.href = `${env.BASENAME}/login${currentPath}`;
};

class ApiCaller {
  private cancelTokens: Array<{ id: string; token: CancelTokenSource }> = [];

  async send(config: Config) {
    const url = `${env.BASENAME}${env.NODE_API}${config.url}`;
    let response;
    let cancelTokenSource: CancelTokenSource | undefined;
    if (config.cancelId) {
      cancelTokenSource = axios.CancelToken.source();
      this.cancelTokens.push({ id: config.cancelId, token: cancelTokenSource });
    }
    try {
      const { quietError, cancelId, ...axiosConfig } = config;
      response = await axios.request({
        ...axiosConfig,
        url,
        cancelToken: cancelTokenSource?.token,
      });
    } catch (error) {
      if (axios.isCancel(error)) {
        return [];
      }

      const showGenericError = (message: string) => {
        if (!config.quietError) {
          notificationStore.alert(message, "error");
        }
      };
      if (error.response) {
        redirect(error.response.status);
        const message =
          error.response.data.error?.message || error.response.statusText;
        const title =
          error.response.data.error?.title || error.response.statusText;

        if (!config.quietError) {
          notificationStore.alert(title, "error", {
            action: {
              label: "View details",
              callback: () => ({
                request: config,
                error: { status: error.response.status, message },
              }),
            },
          });
        }
        throw new Error(message);
      }
      if (error.request) {
        showGenericError("No server response");
        throw error;
      }

      showGenericError(error.message);
      throw new Error(error.message);
    }
    return response.data;
  }

  cancelRequests(ids: string[]) {
    this.cancelTokens
      .filter(token => ids.indexOf(token.id) !== -1)
      .forEach(token => token.token.cancel());
    this.cancelTokens = this.cancelTokens.filter(
      token => ids.indexOf(token.id) === -1,
    );
  }
}

export default new ApiCaller();
